import React, { memo, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dropdown, DropdownProps, Skeleton, Tag, Tree, TreeDataNode, TreeProps } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { timesheetsStore } from 'entities/timesheet';
import { availableUsersByDateService } from 'entities/user';
import { ReactComponent as ArrowIcon } from 'shared/assets/icons/dynamicColor/arrow-bottom.svg';
import { ReactComponent as EmployeeIcon } from 'shared/assets/icons/dynamicColor/user.svg';

import { useDebounceValue } from 'shared/lib/debounce';
import { SearchInput } from 'shared/ui/SearchInput';
import styles from './EmployeeTreeSelect.module.scss';

const EmployeeTreeSelect = (): React.ReactElement => {
  const { t } = useTranslation('Timesheet');

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [retainedSelected, setRetainedSelected] = useState<{ employeeId: string; employeeName: string }[]>([]);

  const topLevelFilters = useStore(timesheetsStore, (state) => state.topLevelFilters);
  const setTopLevelFilters = useStore(timesheetsStore, (state) => state.setTopLevelFilters);
  const range = useStore(timesheetsStore, (state) => state.selectedRange);

  const debouncedSearchValue = useDebounceValue(searchValue, 500);

  const { data: employees, isLoading } = useQuery(
    availableUsersByDateService.queryOptions({
      startDate: range[0].format('YYYY-MM-DD'),
      endDate: range[1].format('YYYY-MM-DD'),
      query: debouncedSearchValue,
    }),
  );

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  };

  const treeData: TreeDataNode[] = useMemo(() => {
    const missingSelected = retainedSelected.filter((sel) => !employees?.some((emp) => emp.id === sel.employeeId));

    const missingNodes = missingSelected
      .filter((sel) => sel.employeeName.toLowerCase().includes(debouncedSearchValue.toLowerCase()))
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName))
      .map((sel) => ({
        title: sel.employeeName,
        key: sel.employeeId,
        className: styles.terminatedUser,
      }));

    const fetchedNodes =
      employees?.map((employee) => ({
        title: `${employee.firstName} ${employee.lastName}`,
        key: employee.id,
      })) ?? [];

    return [...fetchedNodes, ...missingNodes];
  }, [employees, debouncedSearchValue, retainedSelected]);

  const onCheck: TreeProps['onCheck'] = (checked) => {
    const checkedArr = checked as string[];
    setCheckedKeys(checkedArr);

    const allUsers = [
      ...employees.map((e) => ({ employeeId: e.id, employeeName: `${e.firstName} ${e.lastName}` })),
      ...retainedSelected.filter((sel) => !employees.some((e) => e.id === sel.employeeId)),
    ];
    const newPersisted = allUsers.filter((u) => checkedArr.includes(u.employeeId));
    setRetainedSelected(newPersisted);
  };

  const handleOk = (): void => {
    setTopLevelFilters({ userIds: checkedKeys });
    setSearchValue('');
    setOpen(false);
  };

  const handleReset = (): void => {
    setCheckedKeys([]);
    setRetainedSelected([]);
    setSearchValue('');
  };

  const renderPopupContent = (): React.ReactNode => (
    <div className={styles.popupContainer}>
      <div className={styles.searchContainer}>
        <SearchInput
          className={styles.searchInput}
          placeholder={t('employeeSelect.searchInput.placeholder')}
          onChange={({ target }) => setSearchValue(target.value)}
          value={searchValue}
          allowClear
        />
      </div>
      {isLoading ? (
        <Skeleton loading={isLoading} active paragraph={{ rows: 9, width: '100%' }} className={styles.skeleton} />
      ) : (
        <Tree
          checkable
          checkedKeys={checkedKeys}
          onCheck={onCheck}
          treeData={treeData}
          height={350}
          virtual={false}
          className={styles.tree}
          showIcon={false}
          switcherIcon={null}
          selectable={false}
        />
      )}
      <div className={styles.popupFooter}>
        <Button type="text" size="small" className={styles.okBtn} onClick={handleReset}>
          {t('employeeSelect.resetBtn.text')}
        </Button>
        <Button type="primary" size="small" className={styles.okBtn} onClick={handleOk}>
          {t('employeeSelect.okBtn.text')}
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      popupRender={renderPopupContent}
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      destroyOnHidden
    >
      <Button
        className={classNames(styles.dropdownBtn, { [styles.activeDropdownBtn]: open })}
        icon={<EmployeeIcon className={styles.icon} />}
      >
        {t('employeeSelect.employeesBtn.text')}
        <Tag className={styles.tag} bordered={false}>
          <span className={styles.tagContent}>{topLevelFilters.userIds?.length}</span>
        </Tag>
        <ArrowIcon className={styles.icon} width={10} height={10} />
      </Button>
    </Dropdown>
  );
};

export default memo(EmployeeTreeSelect);
